/** Price fields shared by machine listing cards and the machine details summary. */
export interface MachineListingSource {
  buyItNowOnly?: boolean;
  status?: string;
  rentalRate?: number;
  rateSchedules?: { label?: string; cost?: number }[];
  buyItNowEnabled?: boolean;
  buyItNowPrice?: number;
  buyItNowPricingOnRequest?: boolean;
  purchasePrice?: number;
}

export interface MachineListingPrice {
  /** Same gate as the details summary: not buy-only, not currently on rent, and a positive monthly rate. */
  showRentalRates: boolean;
  weekly: number | null;
  monthly: number | null;
  /** hidden: no buy offer. price: positive amount. on-request: ask for the price. unlabeled: offer exists but the amount is zero. */
  buyItNow: "hidden" | "price" | "on-request" | "unlabeled";
  buyItNowPrice: number | null;
}

export interface RentToOwnOption {
  price: number | null;
  onRequest: boolean;
}

export function positiveListingAmount(value: unknown): number | null {
  const amount = typeof value === "number" ? value : Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/** WEEK schedule cost from the same search document the details page reads. Zero and missing rates are omitted. */
export function weeklyRentalAmount(machine: MachineListingSource | null | undefined): number | null {
  const week = (machine?.rateSchedules || []).find(schedule => (schedule?.label || "").trim().toUpperCase() === "WEEK");
  return positiveListingAmount(week?.cost);
}

export function monthlyRentalAmount(machine: MachineListingSource | null | undefined): number | null {
  return positiveListingAmount(machine?.rentalRate);
}

export function machineListingPrice(machine: MachineListingSource | null | undefined): MachineListingPrice {
  const monthly = monthlyRentalAmount(machine);
  const weekly = weeklyRentalAmount(machine);
  const showRentalRates = !!machine && !machine.buyItNowOnly && machine.status !== "OnRent" && monthly != null;
  if (!machine?.buyItNowEnabled) {
    return { showRentalRates, weekly: showRentalRates ? weekly : null, monthly: showRentalRates ? monthly : null, buyItNow: "hidden", buyItNowPrice: null };
  }
  if (machine.buyItNowPricingOnRequest) {
    return { showRentalRates, weekly: showRentalRates ? weekly : null, monthly: showRentalRates ? monthly : null, buyItNow: "on-request", buyItNowPrice: null };
  }
  const buyItNowPrice = positiveListingAmount(machine.buyItNowPrice);
  return {
    showRentalRates,
    weekly: showRentalRates ? weekly : null,
    monthly: showRentalRates ? monthly : null,
    buyItNow: buyItNowPrice != null ? "price" : "unlabeled",
    buyItNowPrice,
  };
}

export function roundListingAmountUpToNearest100(value: number): number {
  return Math.ceil(value / 100) * 100;
}

/**
 * Rent-to-own starting price: a positive purchase price, otherwise the buy-it-now price.
 * Price on Request stays unlabeled. A zero amount is not a price.
 */
export function machineRentToOwnOption(machine: MachineListingSource | null | undefined): RentToOwnOption {
  const purchase = positiveListingAmount(machine?.purchasePrice);
  if (purchase != null) return { price: purchase, onRequest: false };
  if (machine?.buyItNowPricingOnRequest) return { price: null, onRequest: true };
  return { price: positiveListingAmount(machine?.buyItNowPrice), onRequest: false };
}

/** Same rental-credit math as the details Rent to Own table: monthly × months × 80%, rounded up to $100. */
export function rentToOwnRentalCredit(monthlyRate: number | null | undefined, months: number, creditPercent = 0.8): number {
  const monthly = positiveListingAmount(monthlyRate);
  if (monthly == null || months <= 0) return 0;
  return roundListingAmountUpToNearest100(monthly * months * creditPercent);
}

export function formatListingUsd(amount: number): string {
  return "$" + Math.round(amount).toLocaleString("en-US");
}
